import React from "react";
import RecordingControls from "../RecordingControls";

export default function SessionRecordingPanel({
  t,
  selectedEntity,
  timer,
  running,
  segmentRecording,
  continuousRecording,
  micClicks,
  handleMicClick,
  startContinuousRecording,
  lastDataSource,
  lastLatencyMs,
  liveNoteText,
  setLiveNoteText,
  noteRecState,
  noteError,
  startNoteRecording,
  stopNoteRecording,
  saveNote,
  notesByEntity,
  currentSessionId,
  deleteNote,
  DEFAULT_AVATAR,
}) {
  const entityId = selectedEntity?.id ? String(selectedEntity.id) : null;
  const entityNotes = entityId ? notesByEntity[entityId] || [] : [];
  const displayNotes = currentSessionId
    ? entityNotes.filter(
        (n) => String(n.session_id) === String(currentSessionId),
      )
    : entityNotes;

  return (
    <aside className="su-right">
      <div className="su-card">
        <h4>{t("Voice Recording")}</h4>
        <p style={{ margin: "8px 0 16px 0", fontSize: 13, color: "var(--edu-text-medium)", lineHeight: 1.5 }}>
          {t("Record voice samples to analyze emotional well-being. Choose from segment recording or continuous capture.")}
        </p>
        
        <RecordingControls
          timer={timer}
          running={running}
          segmentRecording={segmentRecording}
          continuousRecording={continuousRecording}
          micClicks={micClicks}
          onMicClick={handleMicClick}
          selectedEntity={selectedEntity}
          t={t}
        />

          <button
            className="su-rec-btn"
            onClick={startContinuousRecording}
            disabled={!selectedEntity || segmentRecording}
            style={{
              background: continuousRecording ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" : "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
            }}
          >
            <span style={{ fontSize: 18 }}>🎙️</span>
            <div style={{ flex: 1, textAlign: "left" }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{t("Continuous Recording")}</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>{t("40-second single capture")}</div>
            </div>
            <div style={{ 
              background: "rgba(255,255,255,0.2)", 
              padding: "4px 10px", 
              borderRadius: 6, 
              fontWeight: 700,
              fontSize: 13
            }}>
              {continuousRecording ? t("Recording...") : "40s"}
            </div>
          </button>

        {!selectedEntity && (
          <div style={{ 
            marginTop: 12, 
            padding: 12, 
            background: "var(--edu-warning-bg)", 
            borderRadius: 8, 
            fontSize: 12, 
            color: "var(--edu-warning)",
            display: "flex",
            gap: 8,
            alignItems: "center"
          }}>
            <span>⚠️</span>
            <span>{t("Select a student or staff member to start recording")}</span>
          </div>
        )}

        <div style={{ 
          marginTop: 16, 
          paddingTop: 16, 
          borderTop: "1px solid var(--edu-border)",
          display: "flex",
          gap: 16,
          fontSize: 12,
          color: "var(--edu-text-light)"
        }}>
          <div>
            <span style={{ fontWeight: 600 }}>{t("Source")}:</span> {lastDataSource || "—"}
          </div>
          {lastLatencyMs && (
            <div>
              <span style={{ fontWeight: 600 }}>{t("Latency")}:</span> {lastLatencyMs}ms
            </div>
          )}
        </div>
      </div>

      <div className="su-card" style={{ marginTop: 12 }}>
        <h4>{t("Session Notes")}</h4>
        <textarea
          placeholder={t("Live transcription and notes...")}
          style={{ minHeight: 120 }}
          value={liveNoteText}
          onChange={(e) => setLiveNoteText(e.target.value)}
        />
        {noteRecState !== "idle" && (
          <div
            style={{
              marginTop: 8,
              fontSize: 13,
              color: noteRecState === "error" ? "#EF4444" : "#6B7280",
            }}
          >
            {noteRecState === "recording" &&
              t("🎤 Recording… Click Stop to transcribe.")}
            {noteRecState === "transcribing" && t("⏳ Transcribing…")}
            {noteRecState === "error" &&
              (noteError || t("Error occurred"))}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          {noteRecState !== "recording" ? (
            <button className="su-secondary" onClick={startNoteRecording}>
              🎤 {t("Start Note")}
            </button>
          ) : (
            <button className="su-secondary" onClick={stopNoteRecording}>
              ⏹ {t("Stop")}
            </button>
          )}
          <button
            className="su-secondary"
            onClick={() => setLiveNoteText("")}
          >
            {t("Clear")}
          </button>
          <button className="su-primary" onClick={saveNote}>
            {t("Save Note")}
          </button>
        </div>
        <div style={{ marginTop: 12, maxHeight: 220, overflowY: "auto" }}>
          <div
            style={{
              fontSize: 13,
              color: "#1F2937",
              marginBottom: 8,
              fontWeight: 600,
            }}
          >
            {t("Notes History")}
          </div>
          {displayNotes.length > 0 ? (
            displayNotes
              .slice()
              .reverse()
              .map((n, idx) => (
                <div
                  key={n.note_id || idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    padding: "12px",
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                    backgroundColor: "#F9FAFB",
                    borderRadius: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#1F2937",
                        marginBottom: 4,
                        fontWeight: 500,
                      }}
                    >
                      {n.staff_name || "—"} •{" "}
                      <span style={{ color: "#6B7280", fontSize: 12 }}>
                        {(n.created_at || "")
                          .slice(0, 19)
                          .replace("T", " ")}
                      </span>
                    </div>
                    <div style={{ color: "#1F2937", fontSize: 14 }}>
                      {n.note_text || n.text || ""}
                    </div>
                  </div>
                  <div style={{ marginLeft: 8 }}>
                    <button
                      className="su-icon danger"
                      title={t("Delete note")}
                      onClick={() =>
                        deleteNote(
                          n.session_id || currentSessionId,
                          n.note_id || n.id,
                          idx,
                        )
                      }
                      style={{ color: "#6B7280" }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))
          ) : (
            <div
              className="su-empty"
              style={{ paddingTop: 8, color: "#6B7280" }}
            >
              {t("No notes yet.")}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
